/**
 * 통합검색이 우리 `/api/search-all/video-urls` 에 묻는 것 — 소셜 영상의 지금 유효한 재생 주소.
 *
 * ⚠️ 관문 뒤라 로그인 토큰이 필수다(헤더를 빼면 401).
 * ⚠️ 응답 모양은 서버 BFF 가 이미 계약으로 빚어 보낸다 — 여기서는 목록 모양만 지킨다.
 */

import type { VideoUrlResult, VideoUrlRequestItem } from 'src/types/search-all/video-url';

import { bearerAuthHeader } from 'src/lib/firebase/auth-kit';

import { resolveMockVideoUrls } from './search-all-mock';

/**
 * @param waitMs 받는 중인 건을 서버가 기다려 줄 시간(ms) — 0 이면 곧바로 답한다
 * @returns 입력 순서의 결과 — 서버가 설정 전·실패면 빈 목록(화면은 썸네일로 남는다)
 */
export async function fetchVideoUrls(items: VideoUrlRequestItem[], waitMs = 0, signal?: AbortSignal): Promise<VideoUrlResult[]> {
  if (items.length === 0) return [];
  const mocked = resolveMockVideoUrls(items);
  if (mocked) return mocked;

  const res = await fetch(VIDEO_URLS_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeader()) },
    body: JSON.stringify(waitMs > 0 ? { items, waitMs } : { items }),
    signal,
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data?.results) ? (data.results as VideoUrlResult[]) : [];
}

/** 재생 실패 신고 — 결과를 기다리지 않아도 된다 */
export async function reportVideoUrlFailure(id: string, reason: string): Promise<void> {
  await fetch(`${VIDEO_URLS_PATH}/report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeader()) },
    body: JSON.stringify({ id, reason }),
  }).catch(() => undefined);
}

/** ⚠️ 주소는 여기 한 곳뿐이다 */
const VIDEO_URLS_PATH = '/api/search-all/video-urls';
